import React, { useState, useEffect, useCallback } from 'react';
import { 
  Globe, 
  Shield, 
  Users, 
  Server, 
  Mail, 
  Phone, 
  MapPin,
  Building,
  Eye,
  EyeOff
} from 'lucide-react';
import { apiService, DomainHealthData } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

interface DomainDetailProps {
  domainName: string;
}

const DomainDetail: React.FC<DomainDetailProps> = ({ domainName }) => {
  const [domainData, setDomainData] = useState<DomainHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showContacts, setShowContacts] = useState(false);
  const [showNameservers, setShowNameservers] = useState(false);

  const fetchDomainHealth = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getDomainHealth(domainName);
      setDomainData(response.data);
    } catch (err) {
      setError('Failed to load domain health data. Please try again.');
      console.error('Error fetching domain health:', err);
    } finally {
      setLoading(false);
    }
  }, [domainName]);

  useEffect(() => {
    fetchDomainHealth();
  }, [domainName, fetchDomainHealth]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'inactive':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return <LoadingSpinner size="lg" text="Loading domain health data..." />;
  }

  if (error) {
    return <ErrorMessage message={error} onRetry={fetchDomainHealth} />;
  }

  if (!domainData) {
    return <ErrorMessage message="Domain data not found" />;
  }

  const { domain, history, nameservers, contacts, reputation, security_tests } = domainData;
  const latestHistory = history[0]; // Most recent history entry

  return (
    <div className="space-y-6">
      {/* Domain Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Globe className="h-8 w-8 text-blue-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{domain.domain_name}</h1>
            <p className="text-gray-600">Domain Health Information</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Domain Information */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Globe className="h-5 w-5 mr-2" />
              Domain Information
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Domain Name:</span>
                <span className="font-medium">{domain.domain_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">TLD:</span>
                <span className="font-medium">{domain.tld}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Source:</span>
                <span className="font-medium">{domain.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created:</span>
                <span className="font-medium">{formatDate(domain.domain_created_at)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last Updated:</span>
                <span className="font-medium">{formatDate(domain.domain_updated_at)}</span>
              </div>
              {latestHistory && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Owner:</span>
                    <span className="font-medium">{latestHistory.owner || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <div className="flex space-x-1">
                      {Array.isArray(latestHistory.status) ? (
                        latestHistory.status.map((status, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}
                          >
                            {status}
                          </span>
                        ))
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(latestHistory.status || 'unknown')}`}>
                          {latestHistory.status || 'N/A'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Domain Age:</span>
                    <span className="font-medium">
                      {latestHistory.estimated_domain_age} years
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Expires:</span>
                    <span className="font-medium">
                      {latestHistory.domain_expires_at ? formatDate(latestHistory.domain_expires_at) : 'N/A'}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Reputation Score */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Reputation Score
            </h2>
            {reputation.length > 0 ? (
              <div className="space-y-4">
                {reputation.map((rep, index) => (
                  <div key={index} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Overall Score:</span>
                      <span className={`font-bold text-lg ${getScoreColor(rep.overall_score)}`}>
                        {rep.overall_score}/100
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Mail Score:</span>
                        <span className={getScoreColor(rep.mail_score)}>{rep.mail_score}/100</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Security Score:</span>
                        <span className={getScoreColor(rep.security_score)}>{rep.security_score}/100</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Spam Score:</span>
                        <span className={getScoreColor(100 - rep.spam_score)}>{rep.spam_score}/100</span>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">
                      Last checked: {formatDate(rep.last_checked_at)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No reputation data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Security Tests */}
      {security_tests.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Shield className="h-5 w-5 mr-2" />
              Security Tests
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {security_tests.map((test, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{test.test_name}</h3>
                    <span className={`font-bold ${getScoreColor(test.score)}`}>
                      {test.score}/100
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <div>Type: {test.test_type}</div>
                    <div>Code: {test.test_code}</div>
                    <div>Performed: {formatDate(test.performed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Nameservers */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Server className="h-5 w-5 mr-2" />
              Nameservers ({nameservers.length})
            </h2>
            <button
              onClick={() => setShowNameservers(!showNameservers)}
              className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
            >
              {showNameservers ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showNameservers ? 'Hide' : 'Show'} details</span>
            </button>
          </div>
          
          {showNameservers ? (
            <div className="space-y-3">
              {nameservers.map((ns, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">{ns.hostname}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {ns.type}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 space-y-1">
                    <div>IP Address: {ns.ip_address || 'N/A'}</div>
                    <div>Event Date: {formatDate(ns.event_date)}</div>
                    <div>Event Type: {ns.event_type}</div>
                    <div>Vendor: {ns.vendor_name}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nameservers.map((ns, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {ns.hostname}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Contacts */}
      {contacts.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Users className="h-5 w-5 mr-2" />
                Contacts ({contacts.length})
              </h2>
              <button
                onClick={() => setShowContacts(!showContacts)}
                className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
              >
                {showContacts ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                <span>{showContacts ? 'Hide' : 'Show'} details</span>
              </button>
            </div>
            
            {showContacts ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {contacts.map((contact, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900 capitalize">{contact.contact_type}</h3>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                        {contact.event_type}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Building className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">{contact.organization}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">
                          {contact.first_name} {contact.last_name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">{contact.email}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">{contact.phone}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-700">
                          {contact.address1}, {contact.city}, {contact.state} {contact.postal_code}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {contacts.map((contact, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full capitalize"
                  >
                    {contact.contact_type}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainDetail; 